import React, { useCallback, useState } from "react";
import { Collapse } from "antd";

import { useUserContext } from "../contexts/usercontext";
import { useCardsContext } from "../contexts/cardcontext";
import Helpers from "../../services/helpers";
import { ChartResultsButtons } from "../ChartResultsButtons";
import AddAlertModal from "../modals/AddAlertModal";
import { generateCardTitle } from "../../utils/cardList";
import { usePopularChartContext } from "../contexts/popularchartcontext";
import PopularDetailedResultsPanel from "../charts/PopularCardChart/PopularDetailedResultsPanel";
import { EMPTY_ALERT_MODAL_DATA, ENTITY_TYPES } from "utils/constants";
import Toaster from "../../services/toaster";
import Services from "../../services";

const { Panel } = Collapse;

const getPanelHeaderText = (entity, entityType, results, selectedItems, clickDeleteItems) => {
  const name = {
    [ENTITY_TYPES.CARD]: generateCardTitle(entity),
    [ENTITY_TYPES.SEALED_WAX]: Helpers.getSealedWaxName(entity),
  }[entityType];

  return (
    <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", width: "100%" }}>
      <span>
        {`${name}, Sales - ${
          results && results.total ? results.total + " Results" : "No Results"
        } - Last Synced ${Helpers.formatDateWithTimeTZ(entity.last_synced_at)}`}
      </span>
      <div>
        <ChartResultsButtons
          entityType={entityType}
          entity={entity}
          selectedItems={selectedItems}
          clickDeleteItems={clickDeleteItems}
        />
      </div>
    </div>
  );
};

const CompletedResultsList = ({ savedSearchType, onPaginationChange, results, type = ENTITY_TYPES.CARD }) => {
  const { user } = useUserContext();
  const { setHasAlert } = useCardsContext();
  const { setEntityActiveAlert, setNewImage, cards, sealedWaxes } = usePopularChartContext();
  const [entities, identifier, updateInclusionFunc] = {
    [ENTITY_TYPES.CARD]: [cards.sort(Helpers.sortCardsByNameThenGrade), "cardId", Services.bulkUpdateInclusion],
    [ENTITY_TYPES.SEALED_WAX]: [sealedWaxes, "sealedWaxId", Services.bulkUpdateInclusionWax],
  }[type];
  const [alertModalData, setAlertModalData] = useState({ visible: false, [identifier]: null, name: "" });
  const [selectedItems, setSelectedItems] = useState({});

  const clickSelectItem = useCallback(
    (entityId, cardResultId) => {
      const result = { ...selectedItems };

      if (!result[entityId]) {
        result[entityId] = [];
      }

      const index = result[entityId].indexOf(cardResultId);
      if (index >= 0) {
        result[entityId].splice(index, 1);
      } else {
        result[entityId].push(cardResultId);
      }

      setSelectedItems(result);
    },
    [selectedItems],
  );

  const clickDeleteItems = useCallback(
    async (entityId) => {
      try {
        await updateInclusionFunc(selectedItems[entityId], "exclude");

        const result = { ...selectedItems };
        result[entityId] = [];
        setSelectedItems(result);

        // refresh the data
        await onPaginationChange(
          entityId,
          { limit: user.is_admin ? 50 : 10, skip: 0 },
          { sortBy: "end_time", orderBy: "desc" },
          {},
        );

        Toaster.SuccessNotification(`Sales successfully deleted`);
      } catch (e) {
        Toaster.ErrorNotification(`Deleting sales failed`, e.message);
      }
    },
    [selectedItems],
  );

  return (
    <>
      {entities.length > 0 && <h2 style={{ margin: "25px 0" }}>Detailed Results of Completed Sales</h2>}
      <Collapse defaultActiveKey={[]}>
        {entities.length &&
          entities.map((entity) => (
            <Panel
              key={entity.id}
              header={getPanelHeaderText(
                entity,
                type,
                results[entity.id],
                selectedItems[entity.id],
                () => {
                  clickDeleteItems(entity.id);
                },
                (entityId, name) => setAlertModalData({ visible: true, [identifier]: entityId, name }),
              )}
            >
              <PopularDetailedResultsPanel
                entity={entity}
                entityType={type}
                results={results[entity.id]}
                savedSearchType={savedSearchType}
                onPaginationChange={onPaginationChange}
                onImageChange={setNewImage}
                selectedItems={selectedItems[entity.id]}
                clickSelectItem={(cardResultId) => {
                  clickSelectItem(entity.id, cardResultId);
                }}
              />
              {user?.is_admin && (
                <div style={{ textAlign: "right" }}>
                  <ChartResultsButtons
                    entityType={type}
                    entity={entity}
                    selectedItems={selectedItems[entity.id]}
                    clickDeleteItems={() => {
                      clickDeleteItems(entity.id);
                    }}
                  />
                </div>
              )}
            </Panel>
          ))}
      </Collapse>
      <AddAlertModal
        cardId={alertModalData[identifier]}
        visible={alertModalData.visible}
        onCancel={() => setAlertModalData(EMPTY_ALERT_MODAL_DATA)}
        defaultData={{ name: alertModalData.name }}
        onSubmit={() => {
          setHasAlert(alertModalData[identifier]);
          setEntityActiveAlert(alertModalData[identifier]);
        }}
      />
    </>
  );
};

export default CompletedResultsList;
